// src/pages/SettingUserRoles.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Button,
    CircularProgress,
    Alert,
    Stack,
    Divider,
    Chip,
    List,
    ListItem,
    ListItemText,
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Tooltip,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useSelector } from "react-redux";
import {
    fetchClientUsersWithRoles,
    requestAssignUserRoles,
    requestUpdateClientValidationMethod,
    requestAddUsersToClient,
    requestRemoveUserFromClient,
} from "../../library/api/setting-users";

const ALL_ROLES = ["INPUTTER", "REVIEWER", "APPROVER", "ROLE_ADMIN"];

function normalizeClients(rawData) {
    const data = rawData?.data || {};
    const parent = data.parent || null;
    const children = Array.isArray(data.children) ? data.children : [];

    const list = [];

    if (parent) {
        list.push({ ...parent, kind: "parent" });
    }

    for (const child of children) {
        list.push({ ...child, kind: "child" });
    }

    return list.map((client) => ({
        client_id: client.client_id,
        client_name: client.client_name,
        kind: client.kind, // "parent" | "child"
        users: (client.users || []).map((u) => {
            const cleanRoles = (u.user_roles || []).filter(Boolean);
            return {
                ...u,
                user_roles: cleanRoles,
                original_roles: new Set(cleanRoles),
            };
        }),
    }));
}

const emptyUserRow = () => ({
    fullName: "",
    email: "",
    contactNumber: "",
    department: "",
});

export default function SettingUserRoles() {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [removing, setRemoving] = useState({}); // ✅ userUuid -> boolean
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    // Get existing validation mode from Redux
    const account = useSelector((state) => state.account.value);
    const initialValidationMethod = account?.validation_mode || "SIMPLE";

    const [validationMethod, setValidationMethod] = useState(initialValidationMethod);
    const [validationSaving, setValidationSaving] = useState(false);

    // Help dialog open/close
    const [helpOpen, setHelpOpen] = useState(false);
    const handleOpenHelp = () => setHelpOpen(true);
    const handleCloseHelp = () => setHelpOpen(false);

    // Add-user dialog state
    const [addUserOpen, setAddUserOpen] = useState(false);
    const [addUserClient, setAddUserClient] = useState(null); // {client_id, client_name}
    const [addUserRows, setAddUserRows] = useState([emptyUserRow()]);
    const [addUserSaving, setAddUserSaving] = useState(false);

    const openAddUserDialog = (client) => {
        setError(null);
        setNotice(null);
        setAddUserClient({ client_id: client.client_id, client_name: client.client_name });
        setAddUserRows([emptyUserRow()]);
        setAddUserOpen(true);
    };

    const closeAddUserDialog = () => {
        if (addUserSaving) return;
        setAddUserOpen(false);
    };

    const reloadClients = async () => {
        const raw = await fetchClientUsersWithRoles();
        setClients(normalizeClients(raw));
    };

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            setError(null);

            try {
                const raw = await fetchClientUsersWithRoles();
                if (!cancelled) {
                    setClients(normalizeClients(raw));
                }
            } catch (err) {
                if (!cancelled) {
                    const msg =
                        err?.response?.data?.message || err.message || "Failed to load users";
                    setError(msg);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, []);

    // If account changes while this page is open, keep validationMethod in sync
    useEffect(() => {
        if (account?.validation_mode) {
            setValidationMethod(account.validation_mode);
        }
    }, [account?.validation_mode]);

    const toggleRole = (userUuid, role) => {
        setClients((prev) =>
            prev.map((client) => ({
                ...client,
                users: client.users.map((u) => {
                    if (u.user_uuid !== userUuid) return u;
                    const roles = new Set(u.user_roles || []);
                    if (roles.has(role)) roles.delete(role);
                    else roles.add(role);
                    return { ...u, user_roles: Array.from(roles) };
                }),
            }))
        );
    };

    const hasChanges = (user) => {
        const current = new Set(user.user_roles || []);
        const original = user.original_roles || new Set();

        if (current.size !== original.size) return true;
        for (const r of current) {
            if (!original.has(r)) return true;
        }
        return false;
    };

    const handleSave = async (user) => {
        const userUuid = user.user_uuid;
        const roles = user.user_roles || [];

        setSaving((prev) => ({ ...prev, [userUuid]: true }));
        setError(null);
        setNotice(null);

        try {
            await requestAssignUserRoles({ userUuid, roles });

            setClients((prev) =>
                prev.map((client) => ({
                    ...client,
                    users: client.users.map((u) =>
                        u.user_uuid === userUuid
                            ? { ...u, user_roles: roles, original_roles: new Set(roles) }
                            : u
                    ),
                }))
            );
        } catch (err) {
            const msg =
                err?.response?.data?.message || err.message || "Failed to save roles";
            setError(msg);
        } finally {
            setSaving((prev) => ({ ...prev, [userUuid]: false }));
        }
    };

    // ✅ Remove user from a specific client
    const handleRemoveUser = async (client, user) => {
        const clientId = client.client_id;
        const userUuid = user.user_uuid;

        if (!clientId || !userUuid) return;
        if (removing[userUuid]) return;

        setRemoving((prev) => ({ ...prev, [userUuid]: true }));
        setError(null);
        setNotice(null);

        // optimistic UI remove
        const prevClients = clients;
        setClients((prev) =>
            prev.map((c) =>
                c.client_id !== clientId
                    ? c
                    : { ...c, users: (c.users || []).filter((u) => u.user_uuid !== userUuid) }
            )
        );

        try {
            await requestRemoveUserFromClient({ clientId, userUuid });
            setNotice(`User removed from "${client.client_name}".`);

            // safest: reload from server
            await reloadClients();
        } catch (err) {
            // revert on failure
            setClients(prevClients);

            const msg =
                err?.response?.data?.message ||
                err.message ||
                "Failed to remove user from company";
            setError(msg);
        } finally {
            setRemoving((prev) => ({ ...prev, [userUuid]: false }));
        }
    };

    const handleChangeValidationMethod = async (event) => {
        const newValue = event.target.value;
        if (!newValue || newValue === validationMethod) return;
        if (validationSaving) return;

        const previous = validationMethod;
        setValidationMethod(newValue); // optimistic
        setValidationSaving(true);
        setError(null);
        setNotice(null);

        try {
            await requestUpdateClientValidationMethod({ validationMethod: newValue });
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err.message ||
                "Failed to update validation method";
            setError(msg);
            setValidationMethod(previous); // revert on error
        } finally {
            setValidationSaving(false);
        }
    };

    const updateAddUserRow = (idx, key, value) => {
        setAddUserRows((prev) =>
            prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row))
        );
    };

    const addRow = () => setAddUserRows((prev) => [...prev, emptyUserRow()]);

    const removeRow = (idx) => {
        setAddUserRows((prev) => {
            if (prev.length <= 1) return prev;
            return prev.filter((_, i) => i !== idx);
        });
    };

    const normalizedAddUserPayload = useMemo(() => {
        const cleaned = (addUserRows || [])
            .map((r) => ({
                fullName: (r.fullName || "").trim(),
                email: (r.email || "").trim(),
                contactNumber: (r.contactNumber || "").trim(),
                department: (r.department || "").trim(),
            }))
            .filter((r) => r.fullName || r.email || r.contactNumber || r.department)
            .filter((r) => r.email); // require email to send

        return cleaned;
    }, [addUserRows]);

    const canSubmitAddUsers = useMemo(() => {
        if (!addUserClient?.client_id) return false;
        if (addUserSaving) return false;
        if (!normalizedAddUserPayload.length) return false;
        for (const u of normalizedAddUserPayload) {
            if (!u.email || !u.fullName) return false;
        }
        return true;
    }, [addUserClient?.client_id, addUserSaving, normalizedAddUserPayload]);

    const handleSubmitAddUsers = async () => {
        if (!canSubmitAddUsers) return;

        setAddUserSaving(true);
        setError(null);
        setNotice(null);

        try {
            await requestAddUsersToClient({
                clientId: addUserClient.client_id,
                userList: normalizedAddUserPayload,
            });

            setNotice(`Users added to "${addUserClient.client_name}".`);
            setAddUserOpen(false);

            await reloadClients();
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err.message ||
                "Failed to add users to company";
            setError(msg);
        } finally {
            setAddUserSaving(false);
        }
    };

    const parentClient = clients.find((c) => c.kind === "parent") || null;
    const childClients = clients.filter((c) => c.kind === "child");

    const hasAnyClients = !!parentClient || childClients.length > 0;
    const hasAnyUsers =
        hasAnyClients &&
        (parentClient?.users?.length ||
            childClients.some((c) => Array.isArray(c.users) && c.users.length > 0));

    const renderClientTable = (client) => (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
                mt: 2,
                borderColor: client.kind === "parent" ? "primary.main" : "divider",
                borderWidth: client.kind === "parent" ? 2 : 1,
            }}
        >
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell width="24%">User</TableCell>
                        <TableCell width="26%">Email</TableCell>
                        {ALL_ROLES.map((role) => (
                            <TableCell key={role} align="center">
                                {role}
                            </TableCell>
                        ))}
                        <TableCell align="right" width="16%">
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {client.users && client.users.length > 0 ? (
                        client.users.map((user) => (
                            <TableRow key={user.user_uuid}>
                                <TableCell>
                                    <Typography variant="body2">
                                        {user.user_name} {user.last_name}
                                    </Typography>
                                </TableCell>

                                <TableCell>{user.user_email}</TableCell>

                                {ALL_ROLES.map((role) => (
                                    <TableCell key={role} align="center">
                                        <Checkbox
                                            size="small"
                                            checked={(user.user_roles || []).includes(role)}
                                            onChange={() => toggleRole(user.user_uuid, role)}
                                            disabled={!!removing[user.user_uuid]}
                                        />
                                    </TableCell>
                                ))}

                                <TableCell align="right">
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => handleSave(user)}
                                            disabled={
                                                !hasChanges(user) ||
                                                !!saving[user.user_uuid] ||
                                                !!removing[user.user_uuid]
                                            }
                                        >
                                            {saving[user.user_uuid] ? "Saving..." : "Save"}
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={() => handleRemoveUser(client, user)}
                                            disabled={!!removing[user.user_uuid] || !!saving[user.user_uuid]}
                                        >
                                            {removing[user.user_uuid] ? "Removing..." : "Remove"}
                                        </Button>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={ALL_ROLES.length + 3} align="center">
                                No users for this company.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <div className="page-content">
            <Box p={3}>
                {/* Page title + help icon */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <Typography variant="h5">
                        Data Validation & User Roles Management
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label="Help: how validation and roles work"
                        onClick={handleOpenHelp}
                    >
                        <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                </Stack>

                {/* Data Validation Method toggle */}
                <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        justifyContent="space-between"
                        flexWrap="wrap"
                    >
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Data validation method
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Choose how records are validated before they are included in your summaries.
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} alignItems="center">
                            {validationSaving && <CircularProgress size={18} thickness={5} />}
                            <FormControl component="fieldset" size="small">
                                <RadioGroup
                                    row
                                    value={validationMethod || ""}
                                    onChange={handleChangeValidationMethod}
                                >
                                    <FormControlLabel
                                        value="SIMPLE"
                                        control={<Radio size="small" />}
                                        label="Simple"
                                        disabled={validationSaving}
                                    />
                                    <FormControlLabel
                                        value="REVIEW"
                                        control={<Radio size="small" />}
                                        label="Review"
                                        disabled={validationSaving}
                                    />
                                    <FormControlLabel
                                        value="APPROVE"
                                        control={<Radio size="small" />}
                                        label="Approve"
                                        disabled={validationSaving}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Stack>
                    </Stack>
                </Paper>

                {loading && (
                    <Stack alignItems="center" my={3}>
                        <CircularProgress />
                    </Stack>
                )}

                {(error || notice) && (
                    <Box mb={2}>
                        {error && (
                            <Alert severity="error" onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                        {notice && (
                            <Alert
                                severity="success"
                                onClose={() => setNotice(null)}
                                sx={{ mt: error ? 1 : 0 }}
                            >
                                {notice}
                            </Alert>
                        )}
                    </Box>
                )}

                {!loading && !hasAnyClients && (
                    <Typography variant="body2" color="text.secondary">
                        No company data found for this client.
                    </Typography>
                )}

                {/* Parent section */}
                {!loading && parentClient && (
                    <Box mb={4}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                            <Chip
                                label="Parent company"
                                color="primary"
                                size="small"
                                sx={{ fontWeight: 600 }}
                            />
                            <Typography variant="subtitle1">{parentClient.client_name}</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            Client ID: {parentClient.client_id} • {parentClient.users.length} user
                            {parentClient.users.length === 1 ? "" : "s"}
                        </Typography>

                        {renderClientTable(parentClient)}
                    </Box>
                )}

                {/* Divider between parent and subsidiaries */}
                {!loading && childClients.length > 0 && (
                    <Divider sx={{ my: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            Subsidiaries
                        </Typography>
                    </Divider>
                )}

                {/* Subsidiaries section */}
                {!loading &&
                    childClients.map((client, idx) => (
                        <Box key={client.client_id} mb={4}>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ mb: 0.5 }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Chip
                                        label={`Subsidiary #${idx + 1}`}
                                        size="small"
                                        color="default"
                                        variant="outlined"
                                    />
                                    <Typography variant="subtitle1">{client.client_name}</Typography>
                                </Stack>

                                {/* Add User button (subsidiary only) */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => openAddUserDialog(client)}
                                >
                                    Add User
                                </Button>
                            </Stack>

                            {renderClientTable(client)}
                        </Box>
                    ))}

                {!loading && hasAnyClients && !hasAnyUsers && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Companies loaded, but no users found yet.
                    </Typography>
                )}
            </Box>

            {/* HELP DIALOG */}
            <Dialog open={helpOpen} onClose={handleCloseHelp} maxWidth="sm" fullWidth>
                <DialogTitle>How validation & roles work</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="subtitle2" gutterBottom>
                        Data validation methods
                    </Typography>
                    <List dense sx={{ mt: 0 }}>
                        <ListItem sx={{ py: 0.25 }}>
                            <ListItemText
                                primary={
                                    <>
                                        <strong>Simple:</strong> records are counted as soon as they are created.
                                    </>
                                }
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.25 }}>
                            <ListItemText
                                primary={
                                    <>
                                        <strong>Review:</strong> only reviewed records are counted.
                                    </>
                                }
                            />
                        </ListItem>
                        <ListItem sx={{ py: 0.25 }}>
                            <ListItemText
                                primary={
                                    <>
                                        <strong>Approve:</strong> records are counted when reviewed, while
                                        approvers mark them as approved or flagged.
                                    </>
                                }
                            />
                        </ListItem>
                    </List>

                    <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                            User role definitions
                        </Typography>
                        <List dense sx={{ mb: 0 }}>
                            <ListItem sx={{ py: 0.25 }}>
                                <ListItemText primary={<><strong>INPUTTER</strong>: can create / edit records</>} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25 }}>
                                <ListItemText primary={<><strong>REVIEWER</strong>: can review / comment</>} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25 }}>
                                <ListItemText primary={<><strong>APPROVER</strong>: can approve / finalize</>} />
                            </ListItem>
                            <ListItem sx={{ py: 0.25 }}>
                                <ListItemText primary={<><strong>ROLE_ADMIN</strong>: can manage user roles</>} />
                            </ListItem>
                        </List>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseHelp} variant="contained" size="small">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ADD USERS DIALOG */}
            <Dialog open={addUserOpen} onClose={closeAddUserDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    Add users to {addUserClient?.client_name || "Subsidiary"}
                </DialogTitle>

                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add one or more users. Each row requires <strong>Full name</strong> and <strong>Email</strong>.
                    </Typography>

                    <Stack spacing={1.5}>
                        {addUserRows.map((row, idx) => (
                            <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={1.5}
                                    alignItems={{ xs: "stretch", md: "center" }}
                                >
                                    <TextField
                                        label="Full name"
                                        size="small"
                                        value={row.fullName}
                                        onChange={(e) => updateAddUserRow(idx, "fullName", e.target.value)}
                                        sx={{ flex: 1.3 }}
                                    />
                                    <TextField
                                        label="Email"
                                        size="small"
                                        value={row.email}
                                        onChange={(e) => updateAddUserRow(idx, "email", e.target.value)}
                                        sx={{ flex: 1.4 }}
                                    />
                                    <TextField
                                        label="Contact number"
                                        size="small"
                                        value={row.contactNumber}
                                        onChange={(e) => updateAddUserRow(idx, "contactNumber", e.target.value)}
                                        sx={{ flex: 1 }}
                                    />
                                    <TextField
                                        label="Department"
                                        size="small"
                                        value={row.department}
                                        onChange={(e) => updateAddUserRow(idx, "department", e.target.value)}
                                        sx={{ flex: 1 }}
                                    />

                                    <Tooltip
                                        title={addUserRows.length <= 1 ? "At least one row required" : "Remove row"}
                                    >
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => removeRow(idx)}
                                                disabled={addUserRows.length <= 1 || addUserSaving}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Paper>
                        ))}

                        <Box>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={addRow}
                                disabled={addUserSaving}
                            >
                                Add another row
                            </Button>
                        </Box>
                    </Stack>

                    {addUserOpen && normalizedAddUserPayload.length > 0 && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 2, display: "block" }}
                        >
                            Ready to submit: {normalizedAddUserPayload.length} user(s)
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeAddUserDialog} disabled={addUserSaving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitAddUsers}
                        variant="contained"
                        disabled={!canSubmitAddUsers}
                    >
                        {addUserSaving ? "Adding..." : "Add Users"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
